import { axiosInstance, axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { loginSchema } from '@/models/loginSchema';
import { sessionSchemaValid } from '@/models/sessionValidSchema';
import { AuthToken, Login, SessionValid, User, UserPrivilege } from '@/types/models';
import { USER_ROLE } from '@/constants/user';
import { getError } from '@/utils/error-report/getError';
import axios, { HttpStatusCode } from 'axios';
import { showToast } from '@/utils/showToast';
import { setLoginForOldMinerva } from '@/utils/setLoginForOldMinerva';
import { ThunkConfig } from '@/types/store';
import { userSchema } from '@/models/userSchema';
import { ValidationError } from '@/utils/error-report/errorReporting';
import { authTokenSchema } from '@/models/authTokenSchema';
import { z } from 'zod';
import { serializeDate } from '@/utils/time/serializeDate';
import { hasPrivilege } from './user.utils';
import { closeModal, openLoggedInMenuModal } from '../modal/modal.slice';
import { apiPath } from '../apiPath';

const getUserRole = (privileges: UserPrivilege[]): string => {
  if (hasPrivilege(privileges, 'IS_ADMIN')) {
    return USER_ROLE.ADMIN;
  }
  if (hasPrivilege(privileges, 'IS_CURATOR')) {
    return USER_ROLE.CURATOR;
  }

  return USER_ROLE.USER;
};

const getUserData = async (login: string): Promise<User> => {
  const response = await axiosInstance.get<User>(apiPath.user(login), {
    withCredentials: true,
  });

  return response.data;
};

const getUserTokens = async (userId: number): Promise<AuthToken[]> => {
  const response = await axiosInstanceNewAPI.get<AuthToken[]>(apiPath.authTokens(userId), {
    withCredentials: true,
  });
  const isDataValid = validateDataUsingZodSchema(response.data, z.array(authTokenSchema));

  if (isDataValid) {
    return response.data;
  }
  return [];
};

export const login = createAsyncThunk(
  'user/login',
  async (credentials: { login: string; password: string }, { dispatch }) => {
    try {
      const searchParams = new URLSearchParams(credentials);
      const { data } = await axiosInstance.post<Login>(apiPath.postLogin(), searchParams, {
        withCredentials: true,
      });

      const isDataValid = validateDataUsingZodSchema(data, loginSchema);
      const loginName = data.login;

      if (isDataValid) {
        setLoginForOldMinerva(loginName);

        const userData = await getUserData(loginName);
        const role = getUserRole(userData.privileges);

        const authTokens = await getUserTokens(userData.id);

        if (role !== USER_ROLE.ADMIN && role !== USER_ROLE.CURATOR) {
          dispatch(closeModal());
        } else {
          dispatch(openLoggedInMenuModal());
        }

        return {
          login: loginName,
          role,
          userData,
          token: data.token,
          authTokens,
        };
      }

      return undefined;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === HttpStatusCode.Unauthorized) {
          showToast({
            type: 'error',
            message: 'Invalid credentials.',
          });
          return undefined;
        }
      }
      return Promise.reject(getError({ error, prefix: 'Login' }));
    }
  },
);

export const requestResetPassword = createAsyncThunk(
  'user/login',
  async ({ userLogin, adminEmail }: { userLogin: string; adminEmail: string | undefined }) => {
    try {
      await axiosInstance.post<string>(apiPath.postRequestResetPassword(userLogin), {
        withCredentials: true,
      });
      showToast({
        type: 'info',
        message: `Request sent successfully. Please check your email for further instructions.`,
        duration: 30000,
      });
      return null;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        let message = error?.response?.data?.reason;
        if (adminEmail) {
          message += `. Please contact <a href="mailto:${adminEmail}" style="text-decoration: underline; color: #0055bb" target="_blank">system administrator</a> for further assistance.`;
        }
        showToast({
          type: 'error',
          message: `Cannot reset password. ${message}`,
          duration: 30000,
        });
        return null;
      }
      return Promise.reject(getError({ error, prefix: 'Password reset' }));
    }
  },
);

export const confirmResetPassword = createAsyncThunk(
  'user/confirmResetPassword',
  async ({ token, password }: { token: string; password: string }) => {
    try {
      await axiosInstanceNewAPI.post(apiPath.postResetPassword(), { token, password });
      showToast({
        type: 'success',
        message: 'Password has been reset successfully. You can now log in.',
        duration: 10000,
      });
      return null;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error?.response?.data?.reason ?? 'Invalid or expired token.';
        showToast({
          type: 'error',
          message: `Cannot reset password. ${message}`,
          duration: 10000,
        });
        return null;
      }
      return Promise.reject(getError({ error, prefix: 'Password reset' }));
    }
  },
);

export const confirmEmail = createAsyncThunk(
  'user/confirmEmail',
  async ({ login: userLogin, token }: { login: string; token: string }) => {
    try {
      await axiosInstance.post(apiPath.postConfirmEmail(userLogin, token), null, {
        withCredentials: true,
      });
      return null;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error?.response?.data?.reason ?? 'Invalid or expired token.';
        return Promise.reject(new Error(message));
      }
      return Promise.reject(getError({ error, prefix: 'Email confirmation' }));
    }
  },
);

export const getSessionValid = createAsyncThunk('user/getSessionValid', async () => {
  const response = await axiosInstance.get<SessionValid>(apiPath.getSessionValid(), {
    withCredentials: true,
  });

  const isDataValid = validateDataUsingZodSchema(response.data, sessionSchemaValid);

  const {
    data: { login: loginName, token },
  } = response;

  const userData = await getUserData(loginName);
  const role = getUserRole(userData.privileges);

  if (isDataValid) {
    setLoginForOldMinerva(loginName);

    const authTokens = await getUserTokens(userData.id);

    return {
      login: loginName,
      userData,
      role,
      token,
      authTokens,
    };
  }

  return undefined;
});

export const logout = createAsyncThunk('user/logout', async () => {
  try {
    await axiosInstance.post(apiPath.logout(), null, {
      withCredentials: true,
    });

    setLoginForOldMinerva(undefined);
    return undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: 'Log out' }));
  }
});

export const updateUser = createAsyncThunk<undefined, User, ThunkConfig>(
  'users/updateUser',
  // eslint-disable-next-line consistent-return
  async user => {
    try {
      const newUser = await axiosInstance.patch<User>(
        apiPath.updateUser(user.login),
        {
          user: {
            termsOfUseConsent: user.termsOfUseConsent,
            name: user.name,
            surname: user.surname,
            password: user.password,
            email: user.email,
            orcidId: user.orcidId,
          },
        },
        {
          withCredentials: true,
        },
      );

      validateDataUsingZodSchema(newUser.data, userSchema);
      if (user.password !== undefined) {
        showToast({ type: 'success', message: 'Profile updated successfully' });
      } else {
        showToast({ type: 'success', message: 'ToS agreement registered' });
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_BAD_REQUEST') {
        const data = error.response?.data;
        return Promise.reject(new ValidationError(data.reason));
      }
      return Promise.reject(getError({ error }));
    }
  },
);

export const updateUserEmail = createAsyncThunk<undefined, User, ThunkConfig>(
  'users/updateUser',
  // eslint-disable-next-line consistent-return
  async user => {
    try {
      const newUser = await axiosInstance.patch<User>(apiPath.updateUser(user.login), {
        user: {
          active: true,
          connectedToLdap: false,
          email: user.email,
          name: '',
          orcidId: null,
          surname: '',
          termsOfUseConsent: true,
        },
      });
      validateDataUsingZodSchema(newUser.data, userSchema);
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Update email' }));
    }
  },
);

export const getUserAuthTokens = createAsyncThunk('user/authTokens', async (userId: number) => {
  try {
    return getUserTokens(userId);
  } catch (error) {
    return Promise.reject(getError({ error, prefix: 'Update email' }));
  }
});

export const disableAuthToken = createAsyncThunk(
  'user/authTokens/delete',
  async (authToken: AuthToken, { dispatch }) => {
    try {
      await axiosInstanceNewAPI.delete(apiPath.deleteToken(authToken.userId, authToken.id));
      dispatch(getUserAuthTokens(authToken.userId));
      showToast({
        type: 'info',
        message: authToken.active ? `API token has been disabled` : `API token has been removed`,
      });
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Update email' }));
    }
  },
);

type AuthTokenProps = {
  userId: number;
  name: string;
  expirationDate: Date;
};
export const addAuthToken = createAsyncThunk(
  'user/authTokens/add',
  async (authToken: AuthTokenProps) => {
    try {
      const response = await axiosInstanceNewAPI.post(
        apiPath.addToken(authToken.userId),
        {
          name: authToken.name,
          expirationDate: serializeDate(authToken.expirationDate),
        },
        {
          withCredentials: true,
        },
      );

      const isDataValid = validateDataUsingZodSchema(response.data, authTokenSchema);

      if (isDataValid) {
        showToast({
          type: 'info',
          message: `API token has been added`,
        });
        return response.data;
      }
      return Promise.reject(new Error('Invalid response from server.'));
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict) {
        const data = error.response?.data;
        showToast({ type: 'error', message: data.message, duration: 120000 });
        return Promise.resolve();
      }
      return Promise.reject(getError({ error, prefix: 'Failed to add token' }));
    }
  },
);
